import { memo, useCallback, useMemo } from 'react';
import { Box, Button, Flex, Menu, MenuButton, MenuList, Text } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import {
  getBlockDetails,
  getBlockMetadata,
  groupByFilterChange,
  overallGroupByFilter,
  setEmptyData,
} from 'redux/PlanPageSlice';
import { getCompareScenario } from 'redux/ScenarioSlice';
import { getPlanPageThemeColors } from 'redux/ThemeSlice';
import { AppDispatch } from 'utils/GlobalHelpers';

import { PLAN_GROUP_BY_MENU_MIN_WIDTH, PLAN_GROUP_BY_MENU_RADIUS } from '../planGroupByMenuStyles';
import { PLAN_FILTER_PILL_HEIGHT, PLAN_TEXT } from '../planPageRevampUi';
import { PlanGroupByRadioIcon, PlanToolbarChevronDownIcon, PlanToolbarGroupByIcon } from '../icons';

interface PlanGroupByOption {
  value: string | number;
  label: string;
}

interface PlanGroupByMenuItemProperties {
  label: string;
  isSelected: boolean;
  isLast: boolean;
  onSelect: () => void;
}

const PlanGroupByMenuItem = memo(
  ({ label, isSelected, isLast, onSelect }: PlanGroupByMenuItemProperties) => {
    const themeColors = useSelector(getPlanPageThemeColors);

    return (
      <Flex
        alignItems='center'
        justifyContent='space-between'
        gap={3}
        px={4}
        py={3}
        cursor='pointer'
        bg={themeColors.surface}
        borderBottom={isLast ? 'none' : '1px solid'}
        borderColor={themeColors.border}
        onClick={onSelect}
        _hover={{ bg: themeColors.activeBg }}
      >
        <Text
          {...(isSelected ? PLAN_TEXT.bodySemibold : PLAN_TEXT.body)}
          color={isSelected ? themeColors.primary : themeColors.textDark}
          flex='1'
          noOfLines={1}
        >
          {label}
        </Text>
        <PlanGroupByRadioIcon checked={isSelected} color={themeColors.primary} />
      </Flex>
    );
  },
);

PlanGroupByMenuItem.displayName = 'PlanGroupByMenuItem';

const PlanGroupByControl = () => {
  const dispatch: AppDispatch = useDispatch();
  const metadata = useSelector(getBlockMetadata);
  const blockDetails = useSelector(getBlockDetails);
  const selectedGroupBy = useSelector(overallGroupByFilter);
  const compareScenario = useSelector(getCompareScenario);
  const themeColors = useSelector(getPlanPageThemeColors);

  const dimensions = useMemo(() => metadata?.dimensions || [], [metadata?.dimensions]);

  const options = useMemo<PlanGroupByOption[]>(
    () => [
      { value: 'Total', label: 'Total' },
      ...dimensions.map((dimension: any) => ({
        value: dimension.id,
        label: dimension.name,
      })),
    ],
    [dimensions],
  );

  const groupByLabel = useMemo(() => {
    const selectedOption = options.find(
      (option) => String(option.value) === String(selectedGroupBy),
    );
    return selectedOption?.label ?? 'Total';
  }, [options, selectedGroupBy]);

  const handleGroupByChange = useCallback(
    (value: string | number) => {
      if (String(selectedGroupBy) === String(value)) return;
      dispatch(setEmptyData(true));
      dispatch(groupByFilterChange(value));

      if (blockDetails?.model_id && blockDetails?.id) {
        const planFilters = localStorage.getItem('global-modal-filters');
        const data = planFilters ? JSON.parse(planFilters) : {};
        const modelId = String(blockDetails.model_id);
        const currentBlockId = String(blockDetails.id);
        const modalFilters = data[modelId] || {};
        modalFilters[currentBlockId] = {
          ...modalFilters[currentBlockId],
          groupBy: value,
        };
        localStorage.setItem(
          'global-modal-filters',
          JSON.stringify({ ...data, [modelId]: modalFilters }),
        );
      }
    },
    [blockDetails?.id, blockDetails?.model_id, dispatch, selectedGroupBy],
  );

  const isDisabled = Boolean(compareScenario);

  return (
    <Box flexShrink={0}>
      <Menu placement='bottom-start'>
        <MenuButton
          as={Button}
          size='sm'
          h={PLAN_FILTER_PILL_HEIGHT}
          minH={PLAN_FILTER_PILL_HEIGHT}
          px={0}
          py={0}
          w='fit-content'
          {...PLAN_TEXT.body}
          border='none'
          borderRadius='0'
          bg='transparent'
          color={themeColors.textDark}
          display='inline-flex'
          alignItems='center'
          flexWrap='nowrap'
          gap={2}
          lineHeight='1'
          boxShadow='none'
          cursor={isDisabled ? 'not-allowed' : 'pointer'}
          opacity={isDisabled ? 0.5 : 1}
          pointerEvents={isDisabled ? 'none' : 'auto'}
          _hover={{ bg: themeColors.activeBg }}
          _expanded={{ bg: themeColors.activeBg }}
          _focus={{ boxShadow: 'none' }}
        >
          <Flex alignItems='center' gap={3} flexWrap='nowrap' minW={0}>
            <Box flexShrink={0} display='flex' alignItems='center'>
              <PlanToolbarGroupByIcon width={16} height={16} color={themeColors.primary} />
            </Box>
            <Text
              as='span'
              {...PLAN_TEXT.body}
              whiteSpace='nowrap'
              lineHeight='1.2'
              noOfLines={1}
              maxW='220px'
              minW={0}
            >
              Group by : {groupByLabel}
            </Text>
            <Box flexShrink={0} display='flex' alignItems='center'>
              <PlanToolbarChevronDownIcon width={10} height={6} color={themeColors.primary} />
            </Box>
          </Flex>
        </MenuButton>

        <MenuList
          zIndex={1500}
          p={0}
          minW={PLAN_GROUP_BY_MENU_MIN_WIDTH}
          borderRadius={PLAN_GROUP_BY_MENU_RADIUS}
          border='1px solid'
          borderColor={themeColors.border}
          bg={themeColors.surface}
          boxShadow='0px 8px 24px rgba(18, 24, 38, 0.12)'
          overflow='hidden'
        >
          {options.map((option, index) => (
            <PlanGroupByMenuItem
              key={String(option.value)}
              label={option.label}
              isSelected={String(selectedGroupBy) === String(option.value)}
              isLast={index === options.length - 1}
              onSelect={() => handleGroupByChange(option.value)}
            />
          ))}
        </MenuList>
      </Menu>
    </Box>
  );
};

export default memo(PlanGroupByControl);
